import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import TCProductSelectStore, {
    BaseDevice,
    CounterpartDevice,
    DeviceType,
    Grid,
    ProductSelectActions,
    RemoteDevice,
    PriceResponse,
    PriceScope,
    StateResponse,
} from './tc.product.select.store';
import { useStore, waitForStore } from '@tk/utilities/tk.use.store';
import { fetchRequest, setAsyncUrl } from '@tk/utilities/tk.fetch';
import TKDialog from '@tk/components/tk.dialog';
import TCRemoteDevices from './tc.product.select.remote.devices';
import TCGridCollection from './tc.product.select.grid.collection';
import TCCounterpartDevices from './tc.product.select.counterpart.devices';
import TCDeviceQuantityCounter from './tc.device.quantity.counter';

interface Message {
    type: string;
    text: string;
}

export default class TCProductSelect extends TKCustomElementFactory {
    store?: TCProductSelectStore;
    protected unsubscribe?: () => void;

    articleNumberAttribute = 'data-tc-artno';
    imgAttribute = 'data-tc-device-img';
    titleAttribute = 'data-tc-device-title';
    subTitleAttribute = 'data-tc-device-subtitle';
    powerOutputAttribute = 'data-tc-device-power-output';
    quantityAttribute = 'data-tc-device-quantity';
    priceAttribute = 'data-tc-device-price';
    uvpAttribute = 'data-tc-device-price-uvp';
    deviceTypeAttribute = 'data-tc-device-type';
    inclusiveAttribute = 'data-tc-device-inclusive';
    basketUrlAttribute = 'data-tc-basket-url';
    cassetteAttribute = 'data-tc-cassette-device';
    hasSiblingsAttribute = 'data-tc-has-siblings';

    isAsyncAttribute = 'data-tc-async-prices';
    asyncPrices: boolean;
    priceUrlAttribute = 'data-tc-price-url';
    priceUrl: string;
    stateUrlAttribute = 'data-tc-state-url';
    stateUrl: string;

    counterpartUrlAttribute = 'data-tc-counterpart-url';
    counterpartButtonSelector = '[data-tc-show-counterparts]';

    baseDeviceSelector = '[data-tc-device="base"]';
    remoteDeviceSelector = '[data-tc-device="remote"]';
    gridSelector = '[data-tc-device="grid"]';
    counterpartDialogOpenSelector = '[data-tc-show-counterparts]';
    baseRemoteWrapperSelector = '[data-tc-base-remote-wrapper]';
    baseGridWrapperSelector = '[data-tc-base-grid-wrapper]';
    counterpartWrapperSelector = '[data-tc-counterpart-wrapper]';
    counterpartFooterSelector = '[data-tc-counterpart-footer]';
    remoteTemplateSelector = '[data-tc-remote-template]';
    gridTemplateSelector = '[data-tc-grid-template]';
    counterpartTemplateSelector = '[data-tc-counterpart-template]';
    counterpartPlaceholderTemplateSelector = '[data-tc-counterpart-placeholder-template]';
    basketButtonSelector = '[data-tc-basket-button]';
    miniBasketSelector = '[data-tk-minibasket]';
    headerSelector = '[data-tc-prodsel-header]';
    bodySelector = '[data-tc-prodsel-body]';
    messageWrapperSelector = '[data-tc-message-wrapper]';
    increaserSelector = '[data-tk-counter-increaser]';
    decreaserSelector = '[data-tk-counter-decreaser]';

    succesClassName = 'tc-product-select--success';
    errorClassName = 'tc-product-select--error';

    counterpartDialog?: HTMLDialogElement;
    baseDeviceElement: HTMLElement | null;
    baseRemoteWrapper: HTMLElement | null;
    baseGridWrapper: HTMLElement | null;
    counterpartWrapper: HTMLElement | null;
    counterpartFooter: HTMLElement | null;
    remoteTemplate: HTMLTemplateElement | null;
    gridTemplate: HTMLTemplateElement | null;
    counterpartTemplate: HTMLTemplateElement | null;
    counterpartPlaceholderTemplate: HTMLTemplateElement | null;

    totalPriceSelector = '[data-tc-total-price]';
    totalUvpSelector = '[data-tc-total-uvp]';
    totalPriceWrapper: HTMLElement | null;
    totalUvpWrapper: HTMLElement | null;
    messageWrapper: HTMLElement | null;

    hasUvp: boolean;

    loggedInAttribute = 'data-tc-is-logged-in';
    isLoggedIn: boolean = false;

    constructor() {
        super();
        this.asyncPrices = this.hasAttribute(this.isAsyncAttribute);
        this.priceUrl = this.getAttribute(this.priceUrlAttribute) || '';
        this.stateUrl = this.getAttribute(this.stateUrlAttribute) || '';

        this.baseDeviceElement = this.querySelector(this.baseDeviceSelector);
        this.baseRemoteWrapper = this.querySelector(this.baseRemoteWrapperSelector);
        this.baseGridWrapper = this.querySelector(this.baseGridWrapperSelector);
        this.counterpartWrapper = this.querySelector(this.counterpartWrapperSelector);
        this.counterpartFooter = this.querySelector(this.counterpartFooterSelector);

        this.remoteTemplate = document.querySelector(this.remoteTemplateSelector);
        this.gridTemplate = document.querySelector(this.gridTemplateSelector);
        this.counterpartTemplate = document.querySelector(this.counterpartTemplateSelector);
        this.counterpartPlaceholderTemplate = document.querySelector(this.counterpartPlaceholderTemplateSelector);

        this.totalPriceWrapper = this.querySelector(this.totalPriceSelector);
        this.totalUvpWrapper = this.querySelector(this.totalUvpSelector);
        this.messageWrapper = this.querySelector(this.messageWrapperSelector);

        this.hasUvp = !!this.totalUvpWrapper;

        this.isLoggedIn = this.hasAttribute(this.loggedInAttribute);
    }

    async connectedCallback() {
        if (!this.baseDeviceElement) throw new Error('Missing base device element.');
        if (!this.counterpartWrapper) throw new Error('Missing counterpart wrapper.');
        if (!this.counterpartFooter) throw new Error('Missing counterpart footer.');
        if (!this.messageWrapper) throw new Error('Missing message wrapper element.');
        if (this.asyncPrices && !this.priceUrl) throw new Error('Missing price url.');

        await waitForStore('productSelectStore');
        this.store = useStore('productSelectStore') as TCProductSelectStore;
        this.initializeStore();
    }

    disconnectedCallback(): void {
        this.unsubscribe && this.unsubscribe();
    }

    initializeStore() {
        if (!this.store) return;
        this.unsubscribe = this.store.subscribe(({ action }) => this.render(action as ProductSelectActions));
        this.sendInitialDataToStore();
        this.initCounterpartDialog();
    }

    sendInitialDataToStore() {
        this.store?.setLoginStatus(this.isLoggedIn);
        const baseDeviceType = this.baseDeviceElement!.getAttribute(this.deviceTypeAttribute) as DeviceType;
        const isCassette = this.baseDeviceElement!.hasAttribute(this.cassetteAttribute);

        const remoteElement = this.querySelector(this.remoteDeviceSelector);
        const gridElement = this.querySelector(this.gridSelector);
        let remoteDevice: RemoteDevice | null = null;
        let grid: Grid | null = null;

        if (baseDeviceType === DeviceType.INDOOR) {
            remoteElement &&
                (remoteDevice = {
                    parentId: this.baseDeviceElement!.id,
                    inclusive: remoteElement?.getAttribute(this.inclusiveAttribute) === '1',
                    isActive: true,
                    id: remoteElement.id,
                    artNo: remoteElement.getAttribute(this.articleNumberAttribute) || '',
                    title: remoteElement.getAttribute(this.titleAttribute) || '',
                    subtitle: remoteElement.getAttribute(this.subTitleAttribute) || undefined,
                    price: remoteElement.getAttribute(this.priceAttribute) || '',
                    uvpPrice: remoteElement.getAttribute(this.uvpAttribute) || '',
                    image: remoteElement.getAttribute(this.imgAttribute) || undefined,
                    deviceType: DeviceType.REMOTE,
                    hasSiblings: remoteElement.hasAttribute(this.hasSiblingsAttribute),
                });

            gridElement &&
                isCassette &&
                (grid = {
                    parentId: this.baseDeviceElement!.id,
                    inclusive: gridElement?.getAttribute(this.inclusiveAttribute) === '1',
                    isActive: true,
                    id: gridElement.id,
                    artNo: gridElement.getAttribute(this.articleNumberAttribute) || '',
                    title: gridElement.getAttribute(this.titleAttribute) || '',
                    subtitle: gridElement.getAttribute(this.subTitleAttribute) || undefined,
                    price: gridElement.getAttribute(this.priceAttribute) || '',
                    uvpPrice: gridElement.getAttribute(this.uvpAttribute) || '',
                    image: gridElement.getAttribute(this.imgAttribute) || undefined,
                    deviceType: DeviceType.GRID,
                    hasSiblings: gridElement.hasAttribute(this.hasSiblingsAttribute),
                });
        }

        const baseDevice: BaseDevice = {
            id: this.baseDeviceElement!.id,
            quantity: Number(this.baseDeviceElement!.getAttribute(this.quantityAttribute)),
            deviceType: baseDeviceType,
            artNo: this.baseDeviceElement!.getAttribute(this.articleNumberAttribute) || '',
            title: this.baseDeviceElement!.getAttribute(this.titleAttribute) || '',
            subtitle: this.baseDeviceElement!.getAttribute(this.subTitleAttribute) || undefined,
            powerOutput: this.baseDeviceElement!.getAttribute(this.powerOutputAttribute) || '',
            price: this.baseDeviceElement!.getAttribute(this.priceAttribute) || '',
            uvpPrice: this.baseDeviceElement!.getAttribute(this.uvpAttribute) || '',
            image: this.baseDeviceElement!.getAttribute(this.imgAttribute) || undefined,
            remoteDevices: [],
            remotesInitalized: false,
            isCassette: isCassette,
            gridCollection: [],
            gridsInitalized: false,
        };

        remoteDevice && baseDevice.remoteDevices.push(remoteDevice);
        grid && baseDevice.gridCollection.push(grid);
        this.store?.setBaseDevice(baseDevice);
    }

    initCounterpartDialog() {
        const counterpartUrl = this.getAttribute(this.counterpartUrlAttribute);
        if (!counterpartUrl) return;

        fetchRequest({
            requestURL: counterpartUrl,
            resolveHandler: this.handleCounterpartDialog.bind(this),
        });
    }

    handleCounterpartDialog(response: TKResponse) {
        if (!response.success || !response.dataAsHtml) return;
        this.counterpartDialog = TKDialog.renderDialog<HTMLDialogElement>({
            content: response.dataAsHtml,
            className: 'tk-dialog tk-dialog--auto-width tc-product-select--dialog',
            tag: 'dialog',
        });
        this.registerClickListener();
        this.enableCounterpartButtons();
    }

    registerClickListener() {
        this.pushListener({
            event: 'click',
            element: this,
            action: this.handleClick.bind(this),
        });
    }

    handleClick(event: Event) {
        const target = event.target as HTMLElement;

        if (target.closest(this.counterpartDialogOpenSelector) && this.counterpartDialog)
            this.counterpartDialog.showModal();

        if (target.closest(this.basketButtonSelector))
            this.addToBasket(target.closest(this.basketButtonSelector) as HTMLElement);
    }

    enableCounterpartButtons() {
        const buttons = this.querySelectorAll(this.counterpartDialogOpenSelector);
        buttons.forEach((button) => {
            button.removeAttribute('disabled');
        });
        this.baseDeviceElement?.querySelector(`[${this.quantityAttribute}]`)?.removeAttribute('disabled');
        this.baseDeviceElement?.querySelector(this.increaserSelector)?.removeAttribute('disabled');
    }

    render(action: ProductSelectActions) {
        const ignoredActions = new Set([
            ProductSelectActions.SET_REMOTES_BASE,
            ProductSelectActions.SET_REMOTES_COUNTERPART,
            ProductSelectActions.SET_GRIDS_BASE,
            ProductSelectActions.SET_GRIDS_COUNTERPART,
        ]);
        const refreshActions = new Set([
            ProductSelectActions.SET_BASE_QUANTITY,
            ProductSelectActions.SET_COUNTERPART_QUANTITY,
            ProductSelectActions.SET_ACTIVE_REMOTE,
            ProductSelectActions.SET_ACTIVE_GRID,
            ProductSelectActions.SET_COUNTERPART_ACTIVE,
            ProductSelectActions.SET_COUNTERPART_INACTIVE,
            ProductSelectActions.RESET_STORE,
            ProductSelectActions.SET_PRICES,
        ]);
        const recalcActions = new Set([
            !this.asyncPrices && ProductSelectActions.SET_BASE_DEVICE,
            !this.asyncPrices && ProductSelectActions.SET_COUNTERPART_DEVICES,
            ProductSelectActions.SET_ACTIVE_REMOTE,
            ProductSelectActions.SET_ACTIVE_GRID,
            ProductSelectActions.SET_COUNTERPART_ACTIVE,
            ProductSelectActions.SET_COUNTERPART_INACTIVE,
            ProductSelectActions.SET_BASE_QUANTITY,
            ProductSelectActions.SET_COUNTERPART_QUANTITY,
            ProductSelectActions.SET_PRICES,
        ]);
        const priceActions = new Set([
            ProductSelectActions.SET_BASE_DEVICE,
            ProductSelectActions.SET_COUNTERPART_DEVICES,
        ]);
        const stateActions = new Set([ProductSelectActions.SET_COUNTERPART_DEVICES]);

        if (ignoredActions.has(action)) return;
        if (refreshActions.has(action))
            this.refreshProductSelect({ baseGrid: ProductSelectActions.SET_PRICES !== action });
        if (recalcActions.has(action) && this.store?.getLoginStatus()) this.store?.recalcTotal(this.hasUvp);
        if (this.asyncPrices && priceActions.has(action)) this.fetchPrices(action);
        if (this.stateUrl && stateActions.has(action)) this.fetchAccessoryStates();
        if (action === ProductSelectActions.SET_TOTAL_PRICE) this.setTotal();
    }

    setTotal() {
        if (!this.store?.getLoginStatus()) return;
        this.setTotalPrice();
        this.hasUvp && this.setTotalUvp();
    }

    setTotalPrice() {
        const price = this.store?.getTotalPrice();
        if (!price || !this.totalPriceWrapper) return;
        this.totalPriceWrapper!.innerText = TCDeviceQuantityCounter.formatPriceWithApostroph(price);
    }

    setTotalUvp() {
        const price = this.store?.getTotalUvp();
        if (!price) return;
        this.totalUvpWrapper!.innerText = TCDeviceQuantityCounter.formatPriceWithApostroph(price);
    }

    refreshProductSelect(
        options: {
            baseDevice?: boolean;
            baseRemote?: boolean;
            baseGrid?: boolean;
            counterparts?: boolean;
        } = {},
    ) {
        const { baseDevice = true, baseRemote = true, baseGrid = true, counterparts = true } = options;

        const id = this.baseDeviceElement!.id;

        if (baseDevice) this.rerenderBaseDevice(id);
        if (baseRemote) this.rerenderBaseRemote(id);
        if (baseGrid) this.rerenderBaseGrid(id);
        if (counterparts) this.rerenderCounterparts();
    }

    rerenderBaseDevice(id: string) {
        const device = this.store?.getDevice(id) as BaseDevice;
        if (!this.baseDeviceElement) return;
        const quantityWrapper = this.baseDeviceElement.querySelector(`[${this.quantityAttribute}]`) as HTMLInputElement;
        if (quantityWrapper) {
            quantityWrapper.value = device.quantity.toString() || '1';
            const counter = quantityWrapper.closest('[data-tc-counter-type]') as TCDeviceQuantityCounter;
            counter?.checkMinValue(device.quantity);
        }
        const priceWrapper = this.baseDeviceElement.querySelector(`[${this.priceAttribute}]`);
        priceWrapper &&
            (priceWrapper.textContent = TCDeviceQuantityCounter.calculateTotalPrice(
                device.price,
                device.quantity || 1,
            ));
    }

    rerenderBaseRemote(id: string) {
        if (!this.baseRemoteWrapper || !this.remoteTemplate) return;
        const remote = this.store?.getActiveRemote(id);
        if (!remote) return;
        this.renderRemote(remote);
    }

    rerenderBaseGrid(id: string) {
        if (!this.baseGridWrapper || !this.gridTemplate) return;
        const grid = this.store?.getActiveGrid(id);
        if (!grid) return;
        this.renderGrid(grid);
    }

    renderRemote(remote: RemoteDevice) {
        const parent = this.store?.getDevice(remote.parentId) as BaseDevice;
        const quantity = parent.quantity;
        this.baseRemoteWrapper!.innerHTML = '';
        TCRemoteDevices.renderRemote(
            remote,
            this.remoteTemplate!,
            this.baseRemoteWrapper!,
            quantity,
            !!this.store?.getLoginStatus(),
        );
    }

    renderGrid(grid: Grid) {
        const parent = this.store?.getDevice(grid.parentId) as BaseDevice;
        if (!parent.isCassette) return;
        const quantity = parent.quantity;
        this.baseGridWrapper!.innerHTML = '';
        TCGridCollection.renderGrid(
            grid,
            this.gridTemplate!,
            this.baseGridWrapper!,
            quantity,
            !!this.store?.getLoginStatus(),
        );
    }

    rerenderCounterparts() {
        if (!this.counterpartWrapper || !this.counterpartTemplate || !this.counterpartPlaceholderTemplate) return;
        const devicesToRender = this.store?.getDevicesToRender();
        this.counterpartWrapper.innerHTML = '';

        if (devicesToRender?.length && devicesToRender[0].isActive) {
            devicesToRender?.forEach((device) => {
                const isAccessory = 'inclusive' in device;
                if (isAccessory && device.deviceType === DeviceType.REMOTE) {
                    const parent = this.store?.getDevice(device.parentId) as CounterpartDevice;
                    const quantity = parent.quantity;
                    TCRemoteDevices.renderRemote(
                        device,
                        this.remoteTemplate!,
                        this.counterpartWrapper!,
                        quantity,
                        !!this.store?.getLoginStatus(),
                    );
                } else if (isAccessory && device.deviceType === DeviceType.GRID) {
                    const parent = this.store?.getDevice(device.parentId) as CounterpartDevice;
                    const quantity = parent.quantity;
                    TCGridCollection.renderGrid(
                        device,
                        this.gridTemplate!,
                        this.counterpartWrapper!,
                        quantity,
                        !!this.store?.getLoginStatus(),
                    );
                } else {
                    TCCounterpartDevices.renderCounterpart(
                        device as CounterpartDevice,
                        this.counterpartTemplate!,
                        this.counterpartWrapper!,
                        !!this.store?.getLoginStatus(),
                    );
                }
            });
            this.counterpartFooter?.removeAttribute('hidden');
            return;
        }

        const placeholderCounterpart = devicesToRender?.at(0) as CounterpartDevice;
        if (!placeholderCounterpart) return;

        TCCounterpartDevices.renderCounterpart(
            placeholderCounterpart,
            this.counterpartPlaceholderTemplate!,
            this.counterpartWrapper!,
            !!this.store?.getLoginStatus(),
        );
        this.counterpartFooter?.setAttribute('hidden', 'true');
    }

    addToBasket(button: HTMLElement) {
        const url = button.getAttribute(this.basketUrlAttribute);
        const data = {
            prodSelectString: this.store?.gatherBasketData(),
        };

        if (!url || !data.prodSelectString) return;

        fetchRequest({
            requestURL: url,
            resolveHandler: this.reset.bind(this),
            payload: data,
        });
    }

    reset(response: TKResponse) {
        if (!response.success || !response.dataAsJson) return;
        const message = response.dataAsJson as Message;

        this.store?.resetStore();
        const minibasket = document.querySelector(this.miniBasketSelector);
        if (!minibasket) return;
        const eventName = minibasket.getAttribute('data-tk-event-update-name') || 'tk-minibasket-update';
        minibasket.dispatchEvent(new CustomEvent(eventName));

        if (!message.text) return;
        this.messageWrapper!.textContent = TCProductSelect.decodeHtmlEntities(message.text);
        this.classList.add(message.type === 'info' ? this.succesClassName : this.errorClassName);
        setTimeout(() => this.classList.remove(this.succesClassName, this.errorClassName), 7000);
    }

    static decodeHtmlEntities(str: string): string {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = str;
        return textarea.value;
    }

    fetchPrices(action: ProductSelectActions) {
        if (!this.store?.getLoginStatus()) return;
        ProductSelectActions.SET_BASE_DEVICE === action && this.fetchBaseDevicePrices();
        ProductSelectActions.SET_COUNTERPART_DEVICES === action && this.fetchCounterpartPrices();
    }

    fetchBaseDevicePrices() {
        const ids = this.store?.getBaseIds();

        const payload = {
            ids,
        };

        fetchRequest({
            requestURL: this.priceUrl,
            resolveHandler: this.updatePrices.bind(this, PriceScope.BASE),
            payload,
        });
    }

    fetchCounterpartPrices() {
        const ids = this.store?.getCounterpartIds();
        const payload = { ids };
        const placeholder = this.querySelector(this.counterpartPlaceholderTemplateSelector);
        if (placeholder) {
            placeholder.classList.remove('hide');
        } else {
            this.rerenderCounterparts();
        }

        fetchRequest({
            requestURL: this.priceUrl,
            resolveHandler: this.updatePrices.bind(this, PriceScope.COUNTERPART),
            payload,
        });
    }

    updatePrices(scope: PriceScope, response: TKResponse<PriceResponse>) {
        if (!response.success || !response.dataAsJson) return;
        this.store?.setPrices(response.dataAsJson, scope);
    }

    async fetchAccessoryStates() {
        const counterparts = this.store?.getCounterparts();
        if (!counterparts || counterparts.length === 0) return;

        const chunkSize = 10; // Maximale parallele Requests pro Gruppe

        for (let i = 0; i < counterparts.length; i += chunkSize) {
            // Erstelle eine Gruppe (Chunk) von z.B. 10 Geräten
            const chunk = counterparts.slice(i, i + chunkSize);

            // Starte alle Requests für diesen Chunk gleichzeitig
            const chunkPromises = chunk.map((device) => {
                const remoteId = this.store?.getRemoteIds(device.id);
                const gridId = this.store?.getGridIds(device.id);
                const payload = {
                    deviceId: device.id,
                    remoteId: remoteId,
                    gridId: gridId,
                    crypt: this.stateUrl,
                };

                return new Promise<void>((resolve) => {
                    fetchRequest({
                        requestURL: setAsyncUrl(true),
                        payload: payload,
                        resolveHandler: (response: TKResponse<StateResponse>) => {
                            this.setAccessoryState(response);
                            resolve();
                        },
                    });
                });
            });

            // Warte, bis die gesamte 10er-Gruppe fertig ist, bevor die nächste startet
            await Promise.all(chunkPromises);
        }
    }

    setAccessoryState(response: TKResponse<StateResponse>) {
        if (!response.success || !response.dataAsJson) return;
        this.store?.setAccessoryState(response.dataAsJson);
    }
}
